import type { AppNotification } from './types';

export type NotificationTranslate = (key: string, options?: Record<string, unknown>) => string;

export function notificationText(
  t: NotificationTranslate,
  n: AppNotification,
): { title: string; detail: string | null } {
  const who = n.actor?.display_name ?? t('notifications.someone');
  const p = n.payload;
  const text = (key: string): string => (typeof p[key] === 'string' ? p[key] : '');
  // A standard role is an id (translated here); a custom role arrives as its name.
  const role =
    typeof p['role'] === 'string'
      ? t(`roles.${p['role']}`)
      : typeof p['role_name'] === 'string'
        ? p['role_name']
        : '';
  switch (n.type) {
    case 'feedback.submitted':
    case 'feedback.author_reply':
    case 'feedback.assigned':
    case 'feedback.reassigned':
    case 'feedback.reply':
    case 'feedback.status': {
      const number = typeof p['number'] === 'number' ? p['number'] : '';
      const status = text('status');
      const key =
        n.type === 'feedback.submitted'
          ? 'feedbackSubmitted'
          : n.type === 'feedback.author_reply'
            ? 'feedbackAuthorReply'
            : n.type === 'feedback.assigned'
              ? 'feedbackAssigned'
              : n.type === 'feedback.reassigned'
                ? 'feedbackReassigned'
                : n.type === 'feedback.reply'
                  ? 'feedbackReply'
                  : status === 'clarification_needed'
                    ? 'feedbackClarify'
                    : status === 'implemented'
                      ? 'feedbackImplemented'
                      : status === 'not_planned'
                        ? 'feedbackNotPlanned'
                        : 'feedbackStatus';
      return {
        title: t(`notifications.types.${key}`, {
          who,
          number,
          status: status ? t(`feedback.status.${status}`) : '',
        }),
        detail: null,
      };
    }
    case 'invitation.workspace':
      return {
        title: t('notifications.types.invitationWorkspace', { who, name: text('target_name') }),
        detail: role,
      };
    case 'invitation.board':
      return {
        title: t('notifications.types.invitationBoard', { who, name: text('target_name') }),
        detail: role,
      };
    case 'invitation.database':
      return {
        title: t('notifications.types.invitationDatabase', { who, name: text('target_name') }),
        detail: role,
      };
    case 'invitation.accepted':
      return { title: t('notifications.types.invitationAccepted', { who }), detail: role || null };
    case 'invitation.declined':
      return { title: t('notifications.types.invitationDeclined', { who }), detail: null };
    case 'request.join':
      return {
        title: t('notifications.types.requestJoin', { who, name: text('target_name') }),
        detail: role || null,
      };
    case 'request.approved':
      return {
        title: t('notifications.types.requestApproved', { name: text('target_name') }),
        detail: null,
      };
    case 'request.rejected':
      return {
        title: t('notifications.types.requestRejected', { name: text('target_name') }),
        detail: null,
      };
    case 'transfer.requested':
      return {
        title: t('notifications.types.transferRequested', {
          who,
          board: text('board_name'),
          workspace: text('workspace_name'),
        }),
        detail: null,
      };
    case 'transfer.approved':
      return {
        title: t('notifications.types.transferApproved', { board: text('board_name') }),
        detail: null,
      };
    case 'transfer.rejected':
      return {
        title: t('notifications.types.transferRejected', { board: text('board_name') }),
        detail: null,
      };
    case 'task.assigned':
      return {
        title: t('notifications.types.taskAssigned', { who, task: text('card_title') }),
        detail: null,
      };
    case 'comment.reply':
      return {
        title: t('notifications.types.reply', { who, task: text('card_title') }),
        detail: null,
      };
    case 'comment.mention':
      return {
        title: t('notifications.types.mention', { who, task: text('card_title') }),
        detail: null,
      };
    case 'task.updated': {
      const changes = (p['changes'] ?? {}) as Record<string, number>;
      const parts = Object.keys(changes).map((kind) => t(`notifications.changes.${kind}`));
      return {
        title: t('notifications.types.taskUpdated', { task: text('card_title') }),
        detail: parts.join(', ') || null,
      };
    }
    case 'member.role_changed':
      return {
        title: t('notifications.types.roleChanged', { name: text('workspace_name') }),
        detail: role || null,
      };
    case 'board.role_changed':
      return {
        title: t('notifications.types.boardRoleChanged', { name: text('board_name') }),
        detail: role || null,
      };
    case 'member.removed':
      return {
        title: t('notifications.types.memberRemoved', { name: text('workspace_name') }),
        detail: null,
      };
    case 'board.access_removed':
      return {
        title: t('notifications.types.accessRemoved', { name: text('board_name') }),
        detail: null,
      };
    case 'board.owner_assigned':
      return {
        title: t('notifications.types.boardOwnerAssigned', { who, name: text('board_name') }),
        detail: null,
      };
    case 'board.access_revoked':
      // Deliberately without the board: the person may no longer know anything about it.
      return { title: t('notifications.types.boardAccessRevoked', { who }), detail: null };
    case 'workspace.deleted':
      return {
        title: t('notifications.types.workspaceDeleted', { who, name: text('workspace_name') }),
        detail: null,
      };
    default:
      if (n.type.startsWith('audit.')) {
        // An administrative event the workspace policy chose to notify about.
        const key = `audit.events.${n.type.slice('audit.'.length).replace(/\./g, '_')}`;
        const label = t(key);
        const object = text('name') || text('board_name');
        return {
          title: t('notifications.types.administrative', {
            who,
            event: label === key ? n.type.slice('audit.'.length) : label,
          }),
          detail:
            [object, text('subject_name'), text('workspace_name')]
              .filter((x) => x !== '')
              .join(' · ') || null,
        };
      }
      return { title: t('notifications.types.unknown'), detail: null };
  }
}
